import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type EmailTemplateType,
  useAdminEmailTemplateByTypeQuery,
  useAdminSendTestEmailTemplateMutation,
  useAdminUpdateEmailTemplateMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { EmailTemplateForm } from '../../../features/email-templates/components/email-template-form'

export const Route = createFileRoute('/admin/email-templates/$type')({
  component: Page,
})

function Page() {
  const type = Route.useParams().type as EmailTemplateType
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const templateQuery = useAdminEmailTemplateByTypeQuery(type)
  const updateMutation = useAdminUpdateEmailTemplateMutation(type)
  const sendTestMutation = useAdminSendTestEmailTemplateMutation()

  const template = templateQuery.data?.data

  const showMessage = (kind: 'success' | 'error', text: string) => {
    setMessage({ type: kind, text })
    setTimeout(() => setMessage(null), 3000)
  }

  if (templateQuery.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">Loading...</div>
    )
  }

  if (!template) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        Email template not found
      </div>
    )
  }

  const handleSave = (values: { subject: string; body: string; enabled: boolean }) => {
    const body = template.editable
      ? { subject: values.subject, body: values.body, enabled: values.enabled }
      : { enabled: values.enabled }
    updateMutation.mutate(
      { path: { type }, body },
      {
        onSuccess: () => showMessage('success', 'Saved'),
        onError: () => showMessage('error', 'Failed to save'),
      }
    )
  }

  const handleSendTest = (values: { subject: string; body: string }) => {
    sendTestMutation.mutate(
      { path: { type }, body: { subject: values.subject, body: values.body } },
      {
        onSuccess: (res) => showMessage('success', `Test sent to ${res?.data?.sentTo ?? 'you'}`),
        onError: () => showMessage('error', 'Failed to send test'),
      }
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <EmailTemplateForm
        key={template.emailType}
        template={template}
        onSave={handleSave}
        onSendTest={handleSendTest}
        isSaving={updateMutation.isPending}
        isSendingTest={sendTestMutation.isPending}
      />

      {message && (
        <div
          className={`fixed bottom-6 right-6 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            message.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  )
}
